import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useContext, useState } from "react";
import { DeleteOutline, FavoriteBorder } from "@mui/icons-material";
import { FiEdit } from "react-icons/fi";
import { EDIT_APPOINTMENT } from "../../../enums/PermissionTitles";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import useDelete from "../../../hooks/useDelete";
import { FormProvider, useForm } from "react-hook-form";
import useSubmit from "../../master/species/hooks/useSubmit";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import { PET_BIOMETRICS } from "../../../enums/ApiPaths";
import {
  APPOINTMENT_BIOMETRICS_DATA,
  PET_BIOMETRICS_DATA,
} from "../../../enums/QueryKeys";
import SingleBiometricDetails from "./SingleBiometricDetails";
import useUser from "../../../hooks/useUser";

const BIOMETRIC_FIELDS = [
  { key: "temperature", label: "BodyTemperature", testIdKey: "temperature" },
  { key: "heartRate", label: "Puls/Min", testIdKey: "heart-rate" },
  { key: "weight", label: "Mass", testIdKey: "weight" },
  {
    key: "breathingFrequency",
    label: "FrequencyOfBreathing",
    testIdKey: "breathing-frequency",
  },
  { key: "length", label: "length", testIdKey: "length" },
  { key: "height", label: "height", testIdKey: "height" },
];

// outlined: flat bordered card used by the single pet view
export default function BiometricsView({
  data,
  queryKey,
  apiKey,
  id,
  outlined = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();
  const { roles } = useUser();

  const methods = useForm({
    defaultValues: {
      temperature: data?.temperature ?? "",
      heartRate: data?.heartRate ?? "",
      weight: data?.weight ?? "",
      length: data?.length ?? "",
      height: data?.height ?? "",
      height: data?.height ?? "",
    },
  });

  const { deleteFn, ConfirmationDialog } = useDelete(
    data?.id,
    PET_BIOMETRICS,
    queryKey,
    "DeleteBiometricsFieldConfirmation",
    null,
    [queryKey]
  );

  const canEdit = isPermissionAllowed(EDIT_APPOINTMENT) && !roles?.user;

  async function handleDelete() {
    await deleteFn();
  }

  function isEmpty(obj) {
    return Object.values(obj).every((x) => x === null || x === "");
  }

  const submittion = useSubmit(id, apiKey + id, queryKey, [
    APPOINTMENT_BIOMETRICS_DATA + data?.appointment?.id,
    PET_BIOMETRICS_DATA + data?.pet?.id,
  ]);

  const submitData = async (editedBiometrics) => {
    if (!isEmpty(editedBiometrics)) {
      editedBiometrics = {
        ...editedBiometrics,
        id: data?.id,
      };

      submittion.submitCallback(editedBiometrics);
    } else {
      handleSnackbar("PleaseInputBiometricsData", snackbarSeverity.ERROR);
    }
  };

  return (
    <Paper
      elevation={outlined ? 0 : 1}
      sx={{
        mb: 2,
        borderRadius: 3,
        ...(outlined && { border: "1px solid", borderColor: "divider" }),
      }}
      data-testid={`pet.biometrics.entry.${data?.id}`}
    >
      <Stack direction={"row"} alignItems={"center"} spacing={1.5} p={2}>
        <Avatar sx={{ bgcolor: "primary.main", color: "#fff" }}>
          <FavoriteBorder />
        </Avatar>
        <Box flexGrow={1} minWidth={0}>
          <Typography variant="h6" lineHeight={1.2}>
            {t("Biometrics")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data?.createdAt}
          </Typography>
        </Box>
        {canEdit ? (
          <IconButton
            onClick={() => setIsEditing((prev) => !prev)}
            title={t("Edit")}
            color={isEditing ? "primary" : "default"}
            data-testid={`pet.biometrics.entry.${data?.id}.expand-trigger`}
          >
            <FiEdit size={20} />
          </IconButton>
        ) : null}
        <IconButton
          onClick={() => handleDelete()}
          disabled={!canEdit}
          color="error"
          title={t("Delete")}
          data-testid={`pet.biometrics.entry.${data?.id}.delete-trigger`}
        >
          <DeleteOutline />
        </IconButton>
      </Stack>
      <Divider />
      <Box p={2}>
        {isEditing ? (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(submitData)}>
              <SingleBiometricDetails data={data} />
            </form>
          </FormProvider>
        ) : (
          <Grid container spacing={2}>
            {BIOMETRIC_FIELDS.map((field) => (
              <Grid item xs={6} sm={4} key={field.key}>
                <Typography variant="caption" color="text.secondary">
                  {t(field.label)}
                </Typography>
                <Typography
                  data-testid={`pet.biometrics.entry.${data?.id}.${field.testIdKey}-value`}
                >
                  {data?.[field.key] || "-"}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <ConfirmationDialog />
    </Paper>
  );
}
